import { useCallback, useEffect, useMemo, useState } from 'react'
import { ClipboardList, Loader2 } from 'lucide-react'
import { Link } from 'react-router-dom'

import { ReportFilterBar } from '@/components/reports/ReportFilterBar'
import {
  exportPackageRedemptionAuditReport,
  fetchPackageRedemptionAuditReport,
  formatReportDate,
  resolveReportPeriodBounds,
} from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useBranch } from '@/contexts/BranchContext'
import { useReportPanelFilters } from '@/contexts/ReportPreferencesContext'
import { posPackageTypeLabel } from '@/lib/pos/posPackage'

export function PackageRedemptionAuditReportPanel() {
  const { activeBranchId } = useBranch()
  const { period, setPeriod, compare, setCompare, customFrom, setCustomFrom, customTo, setCustomTo } =
    useReportPanelFilters()
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  const reportOptions = useMemo(
    () => ({
      period,
      from: period === 'custom' ? customFrom : bounds?.from,
      to: period === 'custom' ? customTo : bounds?.to,
      compare,
    }),
    [period, customFrom, customTo, bounds, compare],
  )

  const loadReport = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setReport(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchPackageRedemptionAuditReport(reportOptions)
      setReport(data)
    } catch (err) {
      setReport(null)
      setError(apiErrorMessage(err, 'Unable to load package redemption audit.'))
    } finally {
      setLoading(false)
    }
  }, [period, customFrom, customTo, reportOptions])

  useEffect(() => {
    loadReport()
  }, [loadReport, activeBranchId])

  const handleExport = async (format) => {
    setExporting(true)
    setError('')
    try {
      await exportPackageRedemptionAuditReport(format, reportOptions)
    } catch (err) {
      setError(apiErrorMessage(err, `Unable to export ${format.toUpperCase()} report.`))
    } finally {
      setExporting(false)
    }
  }

  return (
    <section className="card-surface p-6 lg:p-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <ClipboardList className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Package redemption audit</h2>
            <p className="text-sm text-muted-foreground">
              All package redemptions in the selected period — services, products, and POS sale links.
            </p>
          </div>
        </div>
        <ReportFilterBar
          period={period}
          compare={compare}
          customFrom={customFrom}
          customTo={customTo}
          onPeriodChange={setPeriod}
          onCompareChange={setCompare}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
          onExportCsv={() => handleExport('csv')}
          onExportPdf={() => handleExport('pdf')}
          exporting={exporting}
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading report…
        </div>
      ) : error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : report ? (
        <>
          <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard label="Redemptions" value={String(report.summary.redemption_count)} />
            <SummaryCard label="Total qty" value={String(report.summary.quantity_total)} />
            <SummaryCard label="Customers" value={String(report.summary.customer_count)} />
            <SummaryCard label="Packages used" value={String(report.summary.package_count)} />
          </div>

          {report.compare_summary ? (
            <p className="mb-4 text-sm text-muted-foreground">
              Compare period: {report.compare_summary.redemption_count} redemptions · qty{' '}
              {report.compare_summary.quantity_total}
            </p>
          ) : null}

          {report.by_type.length > 0 ? (
            <div className="mb-6 overflow-x-auto rounded-2xl border border-border/70">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Redemptions</th>
                    <th className="px-4 py-3 font-medium">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {report.by_type.map((row) => (
                    <tr key={row.package_type} className="border-t border-border/60">
                      <td className="px-4 py-3">{posPackageTypeLabel(row.package_type)}</td>
                      <td className="px-4 py-3">{row.redemption_count}</td>
                      <td className="px-4 py-3">{row.quantity_total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <div className="overflow-x-auto rounded-2xl border border-border/70">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Package</th>
                  <th className="px-4 py-3 font-medium">Item</th>
                  <th className="px-4 py-3 font-medium">Qty</th>
                  <th className="px-4 py-3 font-medium">Sale</th>
                  <th className="px-4 py-3 font-medium">By</th>
                </tr>
              </thead>
              <tbody>
                {report.redemptions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                      No package redemptions in this period.
                    </td>
                  </tr>
                ) : (
                  report.redemptions.map((row) => (
                    <tr key={row.id} className="border-t border-border/60">
                      <td className="px-4 py-3">{formatReportDate(row.redeemed_at)}</td>
                      <td className="px-4 py-3">
                        <Link
                          to="/customers"
                          className="font-medium text-primary hover:underline"
                        >
                          {row.customer_name}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <div>{row.package_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {posPackageTypeLabel(row.package_type)}
                        </div>
                      </td>
                      <td className="px-4 py-3">{row.item_name}</td>
                      <td className="px-4 py-3">{row.quantity}</td>
                      <td className="px-4 py-3">{row.sale_number ?? '—'}</td>
                      <td className="px-4 py-3">{row.redeemed_by ?? '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  )
}

/**
 * @param {{ label: string, value: string }} props
 */
function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-muted/20 px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  )
}
